// frontend/src/pages/Products.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  Star, 
  ShoppingCart,
  Heart,
  Eye,
  X,
  SortAsc,
  SortDesc
} from 'lucide-react';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [favorites, setFavorites] = useState(new Set());

  const category = searchParams.get('category');
  const search = searchParams.get('search');
  const sort = searchParams.get('sort') || '-created_at';
  const price_min = searchParams.get('price_min');
  const price_max = searchParams.get('price_max');

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [category, search, sort, price_min, price_max]);

  const fetchProducts = async () => {
    try {
      const params = {};
      if (category) params.category = category;
      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (price_min) params.price_min = price_min;
      if (price_max) params.price_max = price_max;
      
      const response = await apiService.getProducts(params);
      setProducts(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCategories();
      setCategories(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchValue = formData.get('search');
    
    const params = new URLSearchParams(searchParams);
    if (searchValue) {
      params.set('search', searchValue);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleCategoryChange = (categorySlug) => {
    const params = new URLSearchParams(searchParams);
    
    if (categorySlug) {
      params.set('category', categorySlug);
    } else {
      params.delete('category');
    }
    
    setSearchParams(params);
  };

  const handleSortChange = (newSort) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', newSort);
    setSearchParams(params);
  };

  const handlePriceFilter = (min, max) => {
    const params = new URLSearchParams(searchParams);
    
    if (min) {
      params.set('price_min', min);
    } else {
      params.delete('price_min');
    }
    
    if (max) {
      params.set('price_max', max);
    } else {
      params.delete('price_max');
    }
    
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const toggleFavorite = (productId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(productId)) {
      newFavorites.delete(productId);
    } else {
      newFavorites.add(productId);
    }
    setFavorites(newFavorites);
  };

  const getSortOptions = () => [
    { value: '-created_at', label: 'جدیدترین', icon: SortDesc },
    { value: 'created_at', label: 'قدیمی‌ترین', icon: SortAsc },
    { value: '-price', label: 'گرانترین', icon: SortDesc },
    { value: 'price', label: 'ارزانترین', icon: SortAsc },
    { value: 'name', label: 'الفبایی (صعودی)', icon: SortAsc },
    { value: '-name', label: 'الفبایی (نزولی)', icon: SortDesc },
  ];

  const getActiveFiltersCount = () => {
    let count = 0;
    if (category) count++;
    if (price_min || price_max) count++;
    if (search) count++;
    return count;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  const getSEOTitle = () => {
    if (search) {
      return `نتایج جستجو برای "${search}" - محصولات BusinessPro`;
    }
    if (category) {
      const categoryName = categories.find(c => c.slug === category)?.name;
      return `${categoryName} - محصولات صنعتی BusinessPro`;
    }
    return 'محصولات صنعتی - BusinessPro';
  };

  const getSEODescription = () => {
    if (search) {
      return `جستجو و مشاهده محصولات مرتبط با "${search}" در شرکت صنعتی BusinessPro. کیفیت بالا و قیمت مناسب.`;
    }
    if (category) {
      const categoryName = categories.find(c => c.slug === category)?.name;
      return `مشاهده انواع محصولات ${categoryName} با کیفیت از گروه ثنعتی تکنو پلیمر اسپادانا. تنوع بالا و قیمت مناسب.`;
    }
    return 'مشاهده انواع محصولات صنعتی با کیفیت از شرکت تکنو پلیمر اسپادانا. تنوع بالا، قیمت مناسب و گارانتی معتبر.';
  };

  return (
    <>
      <SEOHead 
        title={getSEOTitle()}
        description={getSEODescription()}
        keywords="طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی ،قالب سازی، لاستیک سازی، اورینگ، سیلیکون، پکینگ، دیافراگم، واشر"
        image={getFullImageUrl('/static/images/og-products.jpg')}
      />
    <div className="min-h-screen bg-black py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">🛍️ محصولات ما</h1>
          <p className="text-gray-400 text-lg">با کیفیت ترین محصولات صنعتی</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-gray-900 rounded-2xl p-6 mb-8 border border-gray-800">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <input
                  type="text"
                  name="search"
                  placeholder="جستجو در محصولات..."
                  defaultValue={search}
                  className="w-full lg:w-80 pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
            </form>

            <div className="flex items-center space-x-4 space-x-reverse">
              {/* Sort Options */}
              <select
                value={sort}
                onChange={(e) => handleSortChange(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
              >
                {getSortOptions().map(option => {
                  const Icon = option.icon;
                  return (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  );
                })}
              </select>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2 space-x-reverse bg-gray-800 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                  }`}
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                  }`}
                >
                  <List size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Categories and Price Filters */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Categories Filter */}
            <div>
              <div className="flex items-center space-x-2 space-x-reverse mb-4">
                <Filter size={20} className="text-gray-400" />
                <span className="font-medium text-gray-300">دسته‌بندی‌ها:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleCategoryChange(null)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    !category 
                      ? 'bg-yellow-400 text-gray-900 shadow-lg' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  همه محصولات
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      category === cat.slug
                        ? 'bg-yellow-400 text-gray-900 shadow-lg'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Filter */}
            <div>
              <div className="flex items-center space-x-2 space-x-reverse mb-4">
                <span className="font-medium text-gray-300">فیلتر قیمت:</span>
              </div>
              <div className="flex items-center space-x-3 space-x-reverse">
                <input
                  type="number"
                  placeholder="حداقل قیمت"
                  value={price_min || ''}
                  onChange={(e) => handlePriceFilter(e.target.value, price_max)}
                  className="w-32 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                />
                <span className="text-gray-400">تا</span>
                <input
                  type="number"
                  placeholder="حداکثر قیمت"
                  value={price_max || ''}
                  onChange={(e) => handlePriceFilter(price_min, e.target.value)}
                  className="w-32 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {getActiveFiltersCount() > 0 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-800">
              <div className="flex items-center space-x-2 space-x-reverse">
                <span className="text-gray-300 text-sm">
                  {products.length} محصول یافت شد
                </span>
                {(search || category || price_min || price_max) && (
                  <span className="text-gray-400 text-sm">
                    ({getActiveFiltersCount()} فیلتر فعال)
                  </span>
                )}
              </div>
              <button
                onClick={clearFilters}
                className="flex items-center space-x-2 space-x-reverse text-gray-400 hover:text-white transition-colors text-sm"
              >
                <X size={16} />
                <span>پاک کردن فیلترها</span>
              </button>
            </div>
          )}
        </div>

        {/* Products Grid/List */}
        {products.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="text-gray-400 h-16 w-16 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">محصولی یافت نشد</h3>
            <p className="text-gray-400 mb-6">لطفاً فیلترهای جستجو را تغییر دهید.</p>
            <button
              onClick={clearFilters}
              className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
            >
              نمایش همه محصولات
            </button>
          </div>
        ) : (
          <div className={
            viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-6'
          }>
            {products.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product} 
                viewMode={viewMode} 
                isFavorite={favorites.has(product.id)}
                onToggleFavorite={() => toggleFavorite(product.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
     </>
  );
};

const ProductCard = ({ product, viewMode, isFavorite, onToggleFavorite }) => {
  if (viewMode === 'list') {
    return (
      <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
        <div className="flex flex-col md:flex-row">
          <div className="md:w-64 flex-shrink-0 relative">
            <img
              src={getFullImageUrl(product.main_image)}
              alt={product.name}
              className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <button
              onClick={onToggleFavorite}
              className={`absolute top-3 right-3 p-2 rounded-full bg-black bg-opacity-50 transition-colors ${
                isFavorite ? 'text-red-500' : 'text-gray-300 hover:text-red-500'
              }`}
            >
              <Heart fill={isFavorite ? 'currentColor' : 'none'} size={20} />
            </button>
            {product.is_featured && (
              <div className="absolute top-3 left-3 bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold">
                ویژه
              </div>
            )}
          </div>
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                  {product.name}
                </h3>
                <p className="text-gray-400 text-sm">
                  {product.category?.name}
                </p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-4 line-clamp-3">{product.short_description}</p>
            
            <div className="flex items-center justify-between">
              {product.price ? (
                <span className="text-2xl font-bold text-yellow-400">
                  {product.price.toLocaleString()} تومان
                </span>
              ) : (
                <span className="text-gray-400">قیمت تماس بگیرید</span>
              )}
              
              <div className="flex items-center space-x-3 space-x-reverse">
                <button className="p-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors">
                  <Eye size={18} />
                </button>
                <Link
                  to={`/products/${product.slug}`}
                  className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-300 transition-colors font-medium"
                >
                  مشاهده جزئیات
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
      <div className="relative h-48 overflow-hidden">
        <img
          src={getFullImageUrl(product.main_image)}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <button
          onClick={onToggleFavorite}
          className={`absolute top-3 right-3 p-2 rounded-full bg-black bg-opacity-50 transition-colors ${
            isFavorite ? 'text-red-500' : 'text-gray-300 hover:text-red-500'
          }`}
        >
          <Heart fill={isFavorite ? 'currentColor' : 'none'} size={20} />
        </button>
        {product.is_featured && (
          <div className="absolute top-3 left-3 bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold">
            ویژه
          </div>
        )}
      </div>
      
      <div className="p-4">
        <h3 className="font-bold text-lg text-white mb-2 group-hover:text-yellow-400 transition-colors line-clamp-2">
          {product.name}
        </h3>
        
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">
          {product.short_description}
        </p>

        {product.category && (
          <div className="flex items-center space-x-2 space-x-reverse mb-3">
            <span className="text-gray-500 text-xs">دسته‌بندی:</span>
            <span className="text-yellow-400 text-xs font-medium">
              {product.category.name}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between">
          {product.price ? (
            <span className="text-yellow-400 font-bold text-lg">
              {product.price.toLocaleString()} تومان
            </span>
          ) : (
            <span className="text-gray-400 text-sm">قیمت تماس بگیرید</span>
          )}
          
          <Link
            to={`/products/${product.slug}`}
            className="text-yellow-400 hover:text-yellow-300 text-sm font-medium border border-yellow-400 px-3 py-1 rounded-lg hover:bg-yellow-400 hover:text-gray-900 transition-all"
          >
            جزئیات
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Products;